import React, { useState } from "react";
import { Star, Trophy, ChevronDown, ChevronUp, CheckCircle, Circle } from "lucide-react";

const getTodayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const formatDueLabel = (dateStr) => {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const today = new Date(new Date().toDateString());
  const diffDays = Math.round((date - today) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays === -1) return "Yesterday";
  if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const ChoreListView = ({
  chores,
  completions,
  members,
  onEditChore,
  onCompleteChore,
  onUncompleteChore,
}) => {
  const [leaderboardPeriod, setLeaderboardPeriod] = useState("week");
  const [showCompleted, setShowCompleted] = useState(false);
  const [completingChoreId, setCompletingChoreId] = useState(null);

  const todayStr = getTodayStr();

  // ── Leaderboard ────────────────────────────────────────────────────────────
  const getLeaderboard = () => {
    let filtered = completions;
    if (leaderboardPeriod === "week") {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 7);
      filtered = completions.filter((c) => new Date(c.completed_date) >= cutoff);
    }
    const pointsByMember = {};
    filtered.forEach((c) => {
      if (c.member_id) {
        pointsByMember[c.member_id] = (pointsByMember[c.member_id] || 0) + c.points;
      }
    });
    return members
      .map((m) => ({ ...m, points: pointsByMember[m.id] || 0 }))
      .sort((a, b) => b.points - a.points);
  };

  const leaderboard = getLeaderboard();
  const maxPoints = Math.max(...leaderboard.map((m) => m.points), 1);

  // ── Group chores ───────────────────────────────────────────────────────────
  const activeChores = chores.filter((c) => !c.completed);
  const completedChores = chores.filter((c) => c.completed);

  const overdue = activeChores
    .filter((c) => c.due_date && c.due_date < todayStr)
    .sort((a, b) => a.due_date.localeCompare(b.due_date));
  const dueToday = activeChores.filter((c) => c.due_date === todayStr);
  const upcoming = activeChores
    .filter((c) => c.due_date && c.due_date > todayStr)
    .sort((a, b) => a.due_date.localeCompare(b.due_date));
  const noDueDate = activeChores.filter((c) => !c.due_date);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleCheckboxTap = (chore) => {
    if (chore.assigned_member_ids?.length === 1) {
      onCompleteChore(chore, chore.assigned_member_ids[0]);
      return;
    }
    setCompletingChoreId(completingChoreId === chore.id ? null : chore.id);
  };

  const handlePickMember = (chore, memberId) => {
    setCompletingChoreId(null);
    onCompleteChore(chore, memberId);
  };

  const getMemberById = (id) => members.find((m) => m.id === id);

  // ── Chore row ──────────────────────────────────────────────────────────────
  const renderChoreRow = (chore) => {
    const isCompleting = completingChoreId === chore.id;
    const dueLabel = formatDueLabel(chore.due_date);
    const isOverdue = chore.due_date && chore.due_date < todayStr && !chore.completed;
    const isDueToday = chore.due_date === todayStr && !chore.completed;

    return (
      <div
        key={chore.id}
        className={`border-b border-gray-100 last:border-0 ${chore.completed ? "opacity-60" : ""}`}
      >
        <div className="flex items-center gap-3 py-3 px-1">
          {/* Checkbox */}
          <button
            onClick={() =>
              chore.completed ? onUncompleteChore(chore) : handleCheckboxTap(chore)
            }
            className="flex-shrink-0 touch-manipulation"
            aria-label={chore.completed ? "Mark incomplete" : "Mark complete"}
          >
            {chore.completed ? (
              <CheckCircle size={24} className="text-green-500" />
            ) : (
              <Circle size={24} className="text-gray-300 hover:text-purple-400" />
            )}
          </button>

          {/* Content — tap to edit */}
          <div className="flex-1 min-w-0 touch-manipulation" onClick={() => onEditChore(chore)}>
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`text-sm font-medium ${
                  chore.completed ? "line-through text-gray-400" : "text-gray-800"
                }`}
              >
                {chore.title}
              </span>
              {chore.category && (
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                  {chore.category}
                </span>
              )}
              {chore.repeat && (
                <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">
                  ↻ {chore.repeat}
                </span>
              )}
            </div>

            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {/* Assigned members */}
              {chore.assigned_member_ids?.map((id) => {
                const m = getMemberById(id);
                return m ? (
                  <span key={id} className="flex items-center gap-1">
                    <span
                      className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
                      style={{ backgroundColor: m.color }}
                    />
                    <span className="text-xs text-gray-500">{m.name}</span>
                  </span>
                ) : null;
              })}

              {/* Completed by */}
              {chore.completed && chore.completed_by_member_id && (() => {
                const m = getMemberById(chore.completed_by_member_id);
                return m ? (
                  <span className="text-xs text-green-600">✓ {m.name}</span>
                ) : null;
              })()}

              {/* Due date chip */}
              {dueLabel && (
                <span
                  className={`text-xs px-1.5 py-0.5 rounded ${
                    isOverdue
                      ? "bg-red-100 text-red-600"
                      : isDueToday
                      ? "bg-orange-100 text-orange-600"
                      : "text-gray-400"
                  }`}
                >
                  {dueLabel}
                </span>
              )}

              {/* Points */}
              <span className="flex items-center gap-0.5 text-xs text-yellow-600">
                <Star size={10} className="fill-yellow-400 text-yellow-400" />
                {chore.points}
              </span>
            </div>
          </div>
        </div>

        {/* "Who did this?" picker */}
        {isCompleting && (
          <div className="pb-3 px-9">
            <p className="text-xs text-gray-500 mb-2">Who completed this?</p>
            <div className="flex flex-wrap gap-2">
              {members.map((m) => (
                <button
                  key={m.id}
                  onClick={() => handlePickMember(chore, m.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm text-white font-medium touch-manipulation"
                  style={{ backgroundColor: m.color }}
                >
                  {m.name}
                </button>
              ))}
              <button
                onClick={() => handlePickMember(chore, null)}
                className="px-3 py-1.5 rounded-full text-sm bg-gray-100 text-gray-600 font-medium touch-manipulation"
              >
                No one specific
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Section ────────────────────────────────────────────────────────────────
  const renderSection = (title, choreList, accent = "gray") => {
    if (choreList.length === 0) return null;
    const accentClass = {
      red: "text-red-600",
      orange: "text-orange-600",
      blue: "text-blue-600",
      gray: "text-gray-500",
    }[accent];

    return (
      <div className="mb-4">
        <h3 className={`text-xs font-semibold uppercase tracking-wider mb-1.5 ${accentClass}`}>
          {title} ({choreList.length})
        </h3>
        <div className="bg-white rounded-lg border border-gray-100 px-3">
          {choreList.map(renderChoreRow)}
        </div>
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Leaderboard */}
      <div className="bg-white rounded-lg border border-gray-100 p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Trophy size={18} className="text-yellow-500" />
            <h3 className="font-semibold text-gray-800">Leaderboard</h3>
          </div>
          <div className="flex gap-1">
            {[
              { id: "week", label: "This Week" },
              { id: "all", label: "All Time" },
            ].map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setLeaderboardPeriod(id)}
                className={`px-2 py-1 rounded text-xs font-medium touch-manipulation ${
                  leaderboardPeriod === id
                    ? "bg-purple-600 text-white"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {leaderboard.every((m) => m.points === 0) ? (
          <p className="text-sm text-gray-400 text-center py-2">
            No points earned yet — complete a chore to get on the board!
          </p>
        ) : (
          <div className="space-y-2.5">
            {leaderboard.map((member, i) => (
              <div key={member.id} className="flex items-center gap-3">
                <span className="text-sm font-bold text-gray-400 w-4 flex-shrink-0">
                  {i + 1}
                </span>
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: member.color }}
                />
                <span className="text-sm text-gray-700 flex-1 truncate">{member.name}</span>
                <div className="flex items-center gap-2">
                  {/* Progress bar */}
                  <div className="w-16 sm:w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${(member.points / maxPoints) * 100}%`,
                        backgroundColor: member.color,
                        opacity: 0.7,
                      }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-gray-700 w-6 text-right">
                    {member.points}
                  </span>
                  <Star size={12} className="fill-yellow-400 text-yellow-400 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Chore list */}
      {activeChores.length === 0 && completedChores.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p className="text-lg mb-1">No chores yet!</p>
          <p className="text-sm">Tap "Add Chore" to get started.</p>
        </div>
      ) : (
        <>
          {renderSection("Overdue", overdue, "red")}
          {renderSection("Due Today", dueToday, "orange")}
          {renderSection("Upcoming", upcoming, "blue")}
          {renderSection("No Due Date", noDueDate, "gray")}

          {/* Completed toggle */}
          {completedChores.length > 0 && (
            <div>
              <button
                onClick={() => setShowCompleted((v) => !v)}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-2 touch-manipulation"
              >
                {showCompleted ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                Completed ({completedChores.length})
              </button>
              {showCompleted && (
                <div className="bg-white rounded-lg border border-gray-100 px-3">
                  {completedChores.map(renderChoreRow)}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ChoreListView;
